import type { DehydratedState } from '@tanstack/react-query';
import { Dexie } from 'dexie';
import { appConfig, type ProductEntityType } from 'shared';
import type { AttachmentBlob, DownloadQueueEntry } from '~/modules/attachment/offline/attachments-db';

type DehydratedQuery = DehydratedState['queries'][number];

/** A key/value row of a per-user zustand store. */
export interface KvRecord {
  /** Store base name, e.g. `seen`, `sync`, `navigation`. */
  key: string;
  /** Serialized zustand state (createJSONStorage handles parse/stringify). */
  value: string;
}

/** Per-query React Query row for product entity queries, stored individually for incremental diffing. */
export interface PersistedQueryRecord {
  /** `rq:<queryHash>`. */
  id: string;
  queryHash: string;
  queryKey: DehydratedQuery['queryKey'];
  state: DehydratedQuery['state'];
  dataUpdatedAt: number;
}

/** A React Query meta row: the cache's bundled queries (every query that is not a product query) and version stamps, or one tab's paused mutations. */
export interface PersistedMetaRecord {
  /** The cache's row: `rq`, with no mutations. A tab's row: `rq:mut:<tabSessionId>`, with no queries. */
  key: string;
  timestamp: number;
  buster: string;
  /** Persisted client cache version (appConfig.clientCacheVersion). Mismatch wipes cached queries. */
  clientCacheVersion?: string;
  /** Persisted global lens schema ordinal. Values behind the bundle trigger boot migration. */
  schemaVersion?: number;
  mutations?: DehydratedState['mutations'];
  bundledQueries: DehydratedQuery[];
}

/** A collaborative document's key in the Yjs tables: its entity. */
export interface YDocKey {
  entityType: ProductEntityType;
  entityId: string;
}

/** One collaborative document: its entity, and the tenant and organization its token and HTTP routes check it in. */
export interface YjsDocScope extends YDocKey {
  tenantId: string;
  organizationId: string;
}

/** A stored document's metadata. Its state lives apart, so eviction, the byte budget and the sign-out list read no state. */
export interface YDocRecord extends YjsDocScope {
  /** The document's generation as the relay announced it; another one means the server reseeded the document. */
  generation: string;
  /** 1 while a `local` row is left: an edit no server answer has proven saved. */
  unsynced: 0 | 1;
  /** Base plus update rows, for the byte budget. */
  bytes: number;
  /** The update rows' share of `bytes`; past a threshold, a trim folds them into the base. */
  updateBytes: number;
  updatedAt: number;
  lastOpenedAt: number;
}

/** A stored document's base: every update the last trim folded. */
export interface YDocStateRecord extends YDocKey {
  state: Uint8Array;
}

/** One update appended after the base. A `local` row holds an edit no server answer has proven saved yet. */
export interface YDocUpdateRecord extends YDocKey {
  id?: number;
  update: Uint8Array;
  local: 0 | 1;
}

/** Why edits can never be saved: the entity was deleted, edit rights were lost, the server reseeded the document, or it refused it. */
export type UnsaveableReason = 'deleted' | 'denied' | 'replaced' | 'refused';

/** Edits that can never be saved, kept until the user copies or discards them. */
export interface UnsaveableYDocRecord extends YjsDocScope {
  id?: number;
  generation: string;
  reason: UnsaveableReason;
  state: Uint8Array;
  at: number;
}

let currentDb: LocalUserDatabase | null = null;
let localUserId: string | null = null;

/** Listeners for a delete from another tab (its hard sign-out), run after this tab closed and unbound the database. */
export const deletedElsewhereListeners = new Set<() => void>();

const localUserListeners = new Set<(userId: string | null) => void>();

/** Fires after the DB is rebound or closed, so callbacks see the live instance via `getLocalUserDb()`. Long-lived holders of a `liveQuery` must re-subscribe here. */
export function subscribeLocalUserChange(listener: (userId: string | null) => void): () => void {
  localUserListeners.add(listener);
  return () => localUserListeners.delete(listener);
}

/** Tells the listeners that another user's database, or none, is bound; local-user-storage runs it once a rebind settled. */
export function notifyLocalUserChange(userId: string | null): void {
  for (const listener of localUserListeners) listener(userId);
}

/** All tables share one version ladder: bump the single `version(n)` here, which means concurrent PRs changing it must serialize. */
export class LocalUserDatabase extends Dexie {
  kv!: Dexie.Table<KvRecord, string>;
  queries!: Dexie.Table<PersistedQueryRecord, string>;
  meta!: Dexie.Table<PersistedMetaRecord, string>;
  /** Attachment file blobs (uploads pending sync + cached downloads). */
  blobs!: Dexie.Table<AttachmentBlob, string>;
  /** Background download queue for offline attachment caching. */
  downloadQueue!: Dexie.Table<DownloadQueueEntry, string>;
  /** Collaborative documents opened for editing: metadata only, so a scan over all of them loads no state. */
  yDocs!: Dexie.Table<YDocRecord, [ProductEntityType, string]>;
  /** Each stored document's base state. */
  yDocStates!: Dexie.Table<YDocStateRecord, [ProductEntityType, string]>;
  /** Append-only update log per stored document, one `add` per row so tabs never overwrite each other's rows. */
  yDocUpdates!: Dexie.Table<YDocUpdateRecord, number>;
  /** Edits that can never be saved, kept until the user copies or discards them; never evicted. */
  unsaveableYDocs!: Dexie.Table<UnsaveableYDocRecord, number>;

  constructor(userId: string) {
    super(`${appConfig.slug}:${userId}`);
    // An older database upgrades in place: Dexie adds the missing tables and indexes, drops the undeclared ones and keeps every other row.
    this.version(3)
      .stores({
        kv: 'key',
        queries: 'id',
        meta: 'key',
        blobs: '&id, attachmentId, organizationId, uploadStatus, [organizationId+source], [organizationId+uploadStatus]',
        downloadQueue: '&id, organizationId, [organizationId+status]',
        yDocs: '[entityType+entityId], lastOpenedAt',
        yDocStates: '[entityType+entityId]',
        yDocUpdates: '++id, [entityType+entityId]',
        unsaveableYDocs: '++id, [entityType+entityId]',
      })
      // No bundle selects a row stored as skipped: deleted, it is queued again by the list write that brings its file.
      .upgrade((tx) =>
        tx
          .table('downloadQueue')
          .filter((row) => row.status === 'skipped')
          .delete(),
      );

    // A delete from another connection is a hard sign-out in another tab: close for good and unbind, so a late write
    // here cannot recreate the database (Dexie's default handler keeps auto-open). Upgrades keep the default: a tab on
    // an older version closes so another tab's upgrade can run, and its next query reopens on the newer version.
    this.on('versionchange', (event) => {
      if (event.newVersion !== null) return;
      this.close();
      if (currentDb !== this) return false;
      currentDb = null;
      localUserId = null;
      for (const listener of deletedElsewhereListeners) listener();
      return false;
    });
  }
}

/** The currently bound per-user DB, or `null` while signed out. */
export function getLocalUserDb(): LocalUserDatabase | null {
  return currentDb;
}

/** Open (or reuse) the per-user DB for `userId`. Idempotent per user; closes another user's. */
export function bindLocalUserDb(userId: string): LocalUserDatabase {
  if (currentDb && localUserId === userId) return currentDb;
  if (currentDb) closeLocalUserDb();
  currentDb = new LocalUserDatabase(userId);
  localUserId = userId;
  return currentDb;
}

/** Close and unbind the current per-user DB; its data stays on disk. */
export function closeLocalUserDb(): void {
  currentDb?.close();
  currentDb = null;
  localUserId = null;
}

/** Close the current per-user DB and delete it from disk: a hard sign-out. */
export async function deleteLocalUserDb(): Promise<void> {
  const userId = localUserId;
  closeLocalUserDb();
  if (userId) await Dexie.delete(`${appConfig.slug}:${userId}`);
}
